import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import PersonaAvatar from '../../components/PersonaAvatar'
import MarkdownRenderer from '../../components/MarkdownRenderer'
import personaXpData from '../../data/persona-xp.json'
import QUEST_TEMPLATES from '../../data/quest-templates'
import type { QuestTemplate } from '../../data/quest-templates'

const QUEST_TYPES = [
  { key: 'research', label: 'Research', color: '#6FB8D6', dims: ['com', 'wis'] },
  { key: 'build', label: 'Build', color: '#5AC884', dims: ['com', 'srv'] },
  { key: 'analyze', label: 'Analyze', color: '#B265D6', dims: ['wis', 'com'] },
  { key: 'review', label: 'Review', color: '#E8935A', dims: ['wis', 'soc'] },
  { key: 'creative', label: 'Creative', color: '#D65A93', dims: ['cre', 'idn'] },
  { key: 'social', label: 'Social', color: '#6FB8D6', dims: ['soc', 'srv'] },
]

const DIM_LABELS: Record<string, string> = {
  com: 'Competence', soc: 'Social', cre: 'Creative',
  wis: 'Wisdom', srv: 'Service', idn: 'Identity',
}

const LEVEL_NAMES: Record<number, string> = {
  0: 'Newcomer', 1: 'Citizen', 2: 'Contributor', 3: 'Steward',
}

const LEVEL_COLORS: Record<number, string> = {
  0: '#6B7280', 1: '#6FB8D6', 2: '#E6C85A', 3: '#E8935A',
}

interface PersonaOption {
  name: string
  level: number
  level_name: string
  bottleneck: string
  top3: { key: string; val: number }[]
  total: number
}

export default function QuestCreatePage() {
  const navigate = useNavigate()
  const [title, setTitle] = useState('')
  const [types, setTypes] = useState<Set<string>>(new Set())
  const [description, setDescription] = useState('')
  const [deliverable, setDeliverable] = useState('')
  const [persona, setPersona] = useState('')
  const [selectedPersonas, setSelectedPersonas] = useState<string[]>([])
  const [difficulty, setDifficulty] = useState('medium')
  const [phases, setPhases] = useState<{ id: string; title: string; tasks: { id: string; title: string; assignee: string }[] }[]>([])
  const [quickTask, setQuickTask] = useState('')
  const [addingPhase, setAddingPhase] = useState(false)
  const [newPhaseTitle, setNewPhaseTitle] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<QuestTemplate | null>(null)
  const [showTemplates, setShowTemplates] = useState(true)
  const [suggestMode, setSuggestMode] = useState(false)
  const [suggestPersona, setSuggestPersona] = useState('')
  const [suggestions, setSuggestions] = useState<any[]>([])
  const [suggestLoading, setSuggestLoading] = useState(false)
  const [suggestMeta, setSuggestMeta] = useState<any>(null)
  const [wizardSessionId] = useState(() => {
    const existing = sessionStorage.getItem('wizard-session-id')
    if (existing) return existing
    const id = crypto.randomUUID()
    sessionStorage.setItem('wizard-session-id', id)
    return id
  })
  const [wizardHistory, setWizardHistory] = useState<{ role: 'user' | 'assistant'; content: string }[]>([])
  const [wizardInput, setWizardInput] = useState('')
  const [wizardSending, setWizardSending] = useState(false)

  const personas: PersonaOption[] = useMemo(() => {
    return Object.entries(personaXpData.personas).map(([name, data]: [string, any]) => {
      const dims = ['com', 'soc', 'cre', 'wis', 'srv', 'idn'].map(k => ({ key: k, val: data[k] }))
      dims.sort((a, b) => b.val - a.val)
      return {
        name,
        level: data.level,
        level_name: data.level_name,
        bottleneck: data.bottleneck,
        top3: dims.slice(0, 3),
        total: data.total,
      }
    }).sort((a, b) => a.name.localeCompare(b.name))
  }, [])

  const selectedTypes = QUEST_TYPES.filter(t => types.has(t.key))
  const allDims = [...new Set(selectedTypes.flatMap(t => t.dims))]

  const applyTemplate = (template: QuestTemplate) => {
    setSelectedTemplate(template)
    setTitle(template.titlePattern)
    setTypes(new Set(template.types))
    setDescription(template.descriptionTemplate)
    setDeliverable(template.deliverableTemplate)
    setDifficulty(template.difficulty)
    setShowTemplates(false)
  }

  const clearTemplate = () => {
    setSelectedTemplate(null)
    setTitle('')
    setTypes(new Set())
    setDescription('')
    setDeliverable('')
    setDifficulty('medium')
    setShowTemplates(true)
  }

  const [openProjects, setOpenProjects] = useState<any[]>([])

  const fetchSuggestions = async (personaName: string) => {
    setSuggestLoading(true)
    setSuggestions([])
    setSuggestMeta(null)
    setOpenProjects([])
    try {
      const res = await fetch(`/api/quests/suggest/${personaName}`)
      if (res.ok) {
        const data = await res.json()
        setSuggestions(data.suggestions || [])
        setSuggestMeta({ level: data.level, total_xp: data.total_xp, bottleneck: data.bottleneck })
        setOpenProjects(data.openProjects || [])
      }
    } catch { }
    setSuggestLoading(false)
  }

  const applyProject = (proj: any) => {
    setTitle(`Complete: ${proj.title}`)
    setTypes(new Set(['build']))
    setDescription(`Complete the project "${proj.title}". ${proj.description}\n\nFinish all remaining tasks, produce deliverables, and mark the project as completed.`)
    setDeliverable(`Project "${proj.title}" marked as completed with all tasks done. Deliverables attached to the quest showing the completed work.`)
    setDifficulty('medium')
    setPersona(suggestPersona)
    setSuggestMode(false)
    setShowTemplates(false)
    setSelectedTemplate(null)
  }

  const applySuggestion = (s: any) => {
    setTitle(s.title)
    setTypes(new Set(s.types || []))
    setDescription(s.description)
    setDeliverable(s.deliverable)
    setDifficulty(s.difficulty || 'medium')
    setPersona(suggestPersona)
    setSuggestMode(false)
    setShowTemplates(false)
    setSelectedTemplate(null)
  }

  const effectivePersona = selectedPersonas.length > 0 ? selectedPersonas[0] : persona
  const canSubmit = title.trim() && types.size > 0 && description.trim() && deliverable.trim() && (effectivePersona || selectedPersonas.length > 0)

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return
    setSubmitting(true)
    try {
      const res = await fetch('/api/quests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: title.trim(),
          type: [...types].join('+'),
          types: [...types],
          description: description.trim(),
          deliverable: deliverable.trim(),
          persona: effectivePersona,
          personas: selectedPersonas.length > 0 ? selectedPersonas : [persona],
          difficulty,
          ...(phases.length > 0 ? { phases: phases.map(p => ({ ...p, status: 'pending', tasks: p.tasks.map(t => ({ ...t, status: 'pending' })) })) } : {}),
          ...(selectedTemplate ? {
            templateId: selectedTemplate.id,
            rubrics: selectedTemplate.rubrics,
          } : {}),
          ...(wizardHistory.length > 0 ? { wizardHistory, wizardSessionId } : {}),
        }),
      })
      if (res.ok) {
        const data = await res.json()
        sessionStorage.removeItem('wizard-session-id')
        navigate(`/evolve/quests/${data.id || data.quest?.id}`)
      } else {
        const err = await res.json().catch(() => ({}))
        alert(`Quest creation failed: ${err.error || res.status}`)
      }
    } catch (e) {
      alert(`Quest creation failed: network error`)
    }
    setSubmitting(false)
  }

  return (
    <div style={{ minHeight: '100%', background: 'var(--bg-base)', color: 'var(--text-primary)' }}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 24px' }}>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <button
            onClick={() => navigate('/evolve')}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-muted)', fontSize: 12, fontFamily: 'inherit',
              padding: 0, marginBottom: 8,
            }}
          >
            ← Back to Evolve
          </button>
          <h1 style={{
            fontFamily: 'Playfair Display, Georgia, serif',
            fontSize: 24, fontWeight: 700, marginBottom: 4,
          }}>
            Start a Quest
          </h1>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Create a structured challenge for a persona to complete and grow from.
          </p>
        </div>

        {/* Quest Wizard — always present */}
        <WizardChatPanel
          history={wizardHistory}
          setHistory={setWizardHistory}
          input={wizardInput}
          setInput={setWizardInput}
          sending={wizardSending}
          setSending={setWizardSending}
          onClose={null}
          extraBody={{ sessionId: wizardSessionId, ...(selectedPersonas.length > 0 ? { selectedPersonas } : {}) }}
          onQuestData={(data: any) => {
            if (data.title) setTitle(data.title)
            if (data.type) setTypes(new Set(data.type.split('+')))
            if (data.description) setDescription(data.description)
            if (data.deliverable) setDeliverable(data.deliverable)
            if (data.persona) setPersona(data.persona)
            if (data.difficulty) setDifficulty(data.difficulty)
          }}
        />

        {/* Template Picker */}
        {showTemplates && !selectedTemplate && !suggestMode && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Start from a Template</label>
              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  onClick={() => setSuggestMode(true)}
                  style={{
                    background: 'rgba(90,200,132,0.1)', border: '1px solid rgba(90,200,132,0.3)',
                    borderRadius: 6, cursor: 'pointer', padding: '4px 10px',
                    color: '#5AC884', fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
                  }}
                >
                  Suggest Quests for a Persona
                </button>
                <button
                  onClick={() => setShowTemplates(false)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--text-muted)', fontSize: 11, fontFamily: 'inherit',
                  }}
                >
                  or start blank →
                </button>
              </div>
            </div>
            <div style={{
              display: 'grid', gap: 8,
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            }}>
              {QUEST_TEMPLATES.map(t => (
                <div
                  key={t.id}
                  onClick={() => applyTemplate(t)}
                  style={{
                    padding: 12, borderRadius: 8, cursor: 'pointer',
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border)',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLDivElement).style.borderColor = '#856FC8'
                    ;(e.currentTarget as HTMLDivElement).style.background = 'var(--bg-elevated)'
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border)'
                    ;(e.currentTarget as HTMLDivElement).style.background = 'var(--bg-surface)'
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{t.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6, lineHeight: 1.4 }}>
                    {t.description}
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {t.types.map(type => {
                      const qt = QUEST_TYPES.find(q => q.key === type)
                      return qt ? (
                        <span key={type} style={{
                          fontSize: 9, padding: '1px 6px', borderRadius: 6,
                          background: `${qt.color}18`, color: qt.color, fontWeight: 600,
                        }}>
                          {qt.label}
                        </span>
                      ) : null
                    })}
                    <span style={{
                      fontSize: 9, padding: '1px 6px', borderRadius: 6,
                      background: 'var(--bg-elevated)', color: 'var(--text-muted)',
                    }}>
                      {t.difficulty}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AI Quest Suggestion Mode */}
        {suggestMode && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Suggest Quests for a Persona</label>
              <button
                onClick={() => { setSuggestMode(false); setSuggestions([]); setSuggestPersona(''); setSuggestMeta(null) }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text-muted)', fontSize: 11, fontFamily: 'inherit',
                }}
              >
                ← Back to templates
              </button>
            </div>

            {/* Persona picker for suggestions */}
            {!suggestPersona && (
              <div style={{
                display: 'grid', gap: 8,
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                maxHeight: 300, overflowY: 'auto', padding: 4,
              }}>
                {personas.map(p => (
                  <div
                    key={p.name}
                    onClick={() => { setSuggestPersona(p.name); fetchSuggestions(p.name) }}
                    style={{
                      padding: 10, borderRadius: 8, cursor: 'pointer',
                      background: 'var(--bg-surface)', border: '1px solid var(--border)',
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = '#5AC884' }}
                    onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border)' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <PersonaAvatar name={p.name} size={24} />
                      <span style={{ fontSize: 12, fontWeight: 600 }}>
                        {p.name.charAt(0).toUpperCase() + p.name.slice(1)}
                      </span>
                      <span style={{
                        fontSize: 8, padding: '1px 5px', borderRadius: 6,
                        background: `${LEVEL_COLORS[p.level]}18`, color: LEVEL_COLORS[p.level], fontWeight: 600,
                      }}>
                        {LEVEL_NAMES[p.level]}
                      </span>
                    </div>
                    <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 4 }}>
                      {p.total} XP · Needs: {DIM_LABELS[p.bottleneck] || p.bottleneck}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Loading state */}
            {suggestLoading && (
              <div style={{
                padding: 40, textAlign: 'center', color: 'var(--text-muted)',
                background: 'var(--bg-surface)', borderRadius: 8, border: '1px solid var(--border)',
              }}>
                <div style={{ fontSize: 24, marginBottom: 8 }}>🤔</div>
                <div style={{ fontSize: 13 }}>Analyzing {suggestPersona}'s growth profile...</div>
                <div style={{ fontSize: 11, marginTop: 4 }}>Generating personalized quest suggestions</div>
              </div>
            )}

            {/* Suggestions + Projects */}
            {suggestPersona && !suggestLoading && (suggestions.length > 0 || suggestMeta) && (
              <div>
                {/* Persona context bar */}
                {suggestMeta && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
                    padding: '8px 12px', borderRadius: 8,
                    background: 'rgba(90,200,132,0.08)', border: '1px solid rgba(90,200,132,0.2)',
                  }}>
                    <PersonaAvatar name={suggestPersona} size={28} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>
                        {suggestPersona.charAt(0).toUpperCase() + suggestPersona.slice(1)}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                        {suggestMeta.level} · {suggestMeta.total_xp} XP · Bottleneck: {suggestMeta.bottleneck}
                      </div>
                    </div>
                    <button
                      onClick={() => { setSuggestPersona(''); setSuggestions([]); setSuggestMeta(null) }}
                      style={{
                        marginLeft: 'auto', background: 'none', border: 'none',
                        cursor: 'pointer', color: 'var(--text-muted)', fontSize: 11, fontFamily: 'inherit',
                      }}
                    >
                      Change persona
                    </button>
                  </div>
                )}

                {/* Open Projects */}
                {openProjects.length > 0 && (
                  <>
                    <label style={{ ...labelStyle, marginBottom: 8 }}>Open Projects — Convert to Quest</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                      {openProjects.map((proj, i) => (
                        <div
                          key={i}
                          onClick={() => applyProject(proj)}
                          style={{
                            padding: 14, borderRadius: 8, cursor: 'pointer',
                            background: 'var(--bg-surface)', border: '1px solid var(--border)',
                            transition: 'all 0.15s',
                          }}
                          onMouseEnter={e => {
                            (e.currentTarget as HTMLDivElement).style.borderColor = '#E8935A'
                            ;(e.currentTarget as HTMLDivElement).style.background = 'var(--bg-elevated)'
                          }}
                          onMouseLeave={e => {
                            (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border)'
                            ;(e.currentTarget as HTMLDivElement).style.background = 'var(--bg-surface)'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <span style={{ fontSize: 14, fontWeight: 600 }}>{proj.title}</span>
                            <span style={{
                              fontSize: 9, padding: '1px 6px', borderRadius: 6,
                              background: proj.role === 'lead' ? 'rgba(232,147,90,0.15)' : 'rgba(107,114,128,0.15)',
                              color: proj.role === 'lead' ? '#E8935A' : '#6B7280', fontWeight: 600,
                            }}>
                              {proj.role}
                            </span>
                          </div>
                          {proj.description && (
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.4 }}>
                              {proj.description}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* AI Suggestions */}
                {suggestions.length > 0 && (
                  <>
                    <label style={{ ...labelStyle, marginBottom: 8 }}>AI-Suggested Quests</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                      {suggestions.map((s, i) => (
                        <div
                          key={i}
                          onClick={() => applySuggestion(s)}
                          style={{
                            padding: 14, borderRadius: 8, cursor: 'pointer',
                            background: 'var(--bg-surface)', border: '1px solid var(--border)',
                            transition: 'all 0.15s',
                          }}
                          onMouseEnter={e => {
                            (e.currentTarget as HTMLDivElement).style.borderColor = '#5AC884'
                            ;(e.currentTarget as HTMLDivElement).style.background = 'var(--bg-elevated)'
                          }}
                          onMouseLeave={e => {
                            (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border)'
                            ;(e.currentTarget as HTMLDivElement).style.background = 'var(--bg-surface)'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <span style={{ fontSize: 14, fontWeight: 600 }}>{s.title}</span>
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, lineHeight: 1.4 }}>
                            {s.description?.substring(0, 200)}{(s.description?.length || 0) > 200 ? '...' : ''}
                          </div>
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                            {(s.types || []).map((type: string) => {
                              const qt = QUEST_TYPES.find(q => q.key === type)
                              return qt ? (
                                <span key={type} style={{
                                  fontSize: 9, padding: '1px 6px', borderRadius: 6,
                                  background: `${qt.color}18`, color: qt.color, fontWeight: 600,
                                }}>
                                  {qt.label}
                                </span>
                              ) : null
                            })}
                            <span style={{
                              fontSize: 9, padding: '1px 6px', borderRadius: 6,
                              background: 'var(--bg-elevated)', color: 'var(--text-muted)',
                            }}>
                              {s.difficulty}
                            </span>
                            <span style={{ fontSize: 10, color: '#5AC884', marginLeft: 'auto', fontStyle: 'italic' }}>
                              {s.rationale}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => fetchSuggestions(suggestPersona)}
                      disabled={suggestLoading}
                      style={{
                        width: '100%', padding: '10px 0', borderRadius: 8, fontSize: 12,
                        fontWeight: 600, fontFamily: 'inherit', cursor: suggestLoading ? 'default' : 'pointer',
                        background: 'var(--bg-surface)', color: '#5AC884',
                        border: '1px solid rgba(90,200,132,0.3)',
                        transition: 'all 0.15s',
                      }}
                    >
                      {suggestLoading ? 'Generating...' : 'More Quests'}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Active template indicator */}
        {selectedTemplate && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 12px', borderRadius: 8, marginBottom: 16,
            background: 'rgba(133,111,200,0.1)', border: '1px solid rgba(133,111,200,0.3)',
          }}>
            <div style={{ fontSize: 12 }}>
              <span style={{ color: '#856FC8', fontWeight: 600 }}>Template: {selectedTemplate.name}</span>
              <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>
                {selectedTemplate.rubrics.length} evaluation rubrics loaded
              </span>
            </div>
            <button
              onClick={clearTemplate}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-muted)', fontSize: 11, fontFamily: 'inherit',
              }}
            >
              Clear template
            </button>
          </div>
        )}

        {/* Form */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Title */}
          <div>
            <label style={labelStyle}>Quest Title *</label>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Research photo editing APIs for content pipeline"
              style={inputStyle}
            />
          </div>

          {/* Type */}
          <div>
            <label style={labelStyle}>Quest Type(s) * <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>— select all that apply</span></label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {QUEST_TYPES.map(t => {
                const active = types.has(t.key)
                return (
                  <button
                    key={t.key}
                    onClick={() => {
                      const next = new Set(types)
                      if (active) next.delete(t.key)
                      else next.add(t.key)
                      setTypes(next)
                    }}
                    style={{
                      padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                      background: active ? `${t.color}22` : 'var(--bg-elevated)',
                      color: active ? t.color : 'var(--text-muted)',
                      border: `1px solid ${active ? `${t.color}55` : 'var(--border)'}`,
                      cursor: 'pointer', fontFamily: 'inherit',
                      transition: 'all 0.15s',
                    }}
                  >
                    {active ? '✓ ' : ''}{t.label}
                  </button>
                )
              })}
            </div>
            {selectedTypes.length > 0 && (
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 6 }}>
                Grows: {allDims.map(d => DIM_LABELS[d]).join(' + ')}
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <label style={labelStyle}>Description *</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What needs to be done? Be specific about the scope and context."
              rows={5}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          {/* Deliverable */}
          <div>
            <label style={labelStyle}>Expected Deliverable *</label>
            <textarea
              value={deliverable}
              onChange={e => setDeliverable(e.target.value)}
              placeholder="What should the persona produce? e.g. A research report with pricing comparison, or a working API endpoint."
              rows={3}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          {/* Persona Picker — Multi-select */}
          <div>
            <label style={labelStyle}>Assign to Persona(s) *</label>

            {/* Selected personas pills */}
            {selectedPersonas.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                {selectedPersonas.map(name => (
                  <div key={name} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px',
                    background: 'var(--bg-elevated)', borderRadius: 12,
                    border: '1px solid #856FC844', fontSize: 12,
                  }}>
                    <PersonaAvatar name={name} size={16} />
                    <span style={{ fontWeight: 500, textTransform: 'capitalize' }}>{name}</span>
                    <button
                      onClick={() => {
                        setSelectedPersonas(prev => prev.filter(p => p !== name))
                        if (persona === name) setPersona(selectedPersonas.filter(p => p !== name)[0] || '')
                      }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 10, padding: '0 2px', lineHeight: 1 }}
                    >✕</button>
                  </div>
                ))}
              </div>
            )}

            <div style={{
              display: 'grid', gap: 8,
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              maxHeight: 300, overflowY: 'auto', padding: 4,
            }}>
              {personas.map(p => {
                const isSelected = selectedPersonas.includes(p.name)
                const levelColor = LEVEL_COLORS[p.level]
                const matchesType = selectedTypes.length > 0 && allDims.some(d => d === p.bottleneck)
                return (
                  <div
                    key={p.name}
                    onClick={() => {
                      if (isSelected) {
                        setSelectedPersonas(prev => prev.filter(n => n !== p.name))
                        if (persona === p.name) setPersona(selectedPersonas.filter(n => n !== p.name)[0] || '')
                      } else {
                        setSelectedPersonas(prev => [...prev, p.name])
                        if (!persona) setPersona(p.name)
                      }
                    }}
                    style={{
                      padding: 10, borderRadius: 8, cursor: 'pointer',
                      background: isSelected ? 'var(--bg-elevated)' : 'var(--bg-surface)',
                      border: `1px solid ${isSelected ? levelColor : 'var(--border)'}`,
                      transition: 'all 0.15s',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <div style={{
                        width: 16, height: 16, borderRadius: 3, border: `1.5px solid ${isSelected ? '#5AC884' : 'var(--border)'}`,
                        background: isSelected ? '#5AC884' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'white', fontSize: 9, flexShrink: 0,
                      }}>{isSelected ? '✓' : ''}</div>
                      <PersonaAvatar name={p.name} size={20} />
                      <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>{p.name}</span>
                      <span style={{
                        fontSize: 8, padding: '1px 5px', borderRadius: 6,
                        background: `${levelColor}18`, color: levelColor, fontWeight: 600,
                      }}>
                        {LEVEL_NAMES[p.level]}
                      </span>
                      {matchesType && (
                        <span style={{
                          fontSize: 8, padding: '1px 5px', borderRadius: 6,
                          background: 'rgba(92,200,132,0.15)', color: '#5AC884', fontWeight: 700,
                        }}>
                          GOOD MATCH
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 9, color: 'var(--text-muted)', display: 'flex', gap: 8, paddingLeft: 24 }}>
                      <span>Top: {p.top3.map(d => DIM_LABELS[d.key]?.slice(0, 3).toUpperCase()).join(', ')}</span>
                      <span style={{ color: '#ef4444' }}>Needs: {DIM_LABELS[p.bottleneck] || p.bottleneck}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Phases & Tasks Builder */}
          <div>
            <label style={labelStyle}>Phases & Tasks (Optional)</label>

            {phases.map((phase, pi) => (
              <div key={phase.id} style={{
                background: 'var(--bg-surface)', border: '1px solid var(--border)',
                borderLeft: '3px solid #6FB8D6', borderRadius: 10, padding: 14, marginBottom: 8,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>Phase {pi + 1}: {phase.title}</span>
                  <button
                    onClick={() => setPhases(prev => prev.filter(p => p.id !== phase.id))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 10, marginLeft: 'auto' }}
                  >✕</button>
                </div>
                {phase.tasks.map(task => (
                  <div key={task.id} style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 16, marginBottom: 4 }}>
                    <span style={{ fontSize: 12, flex: 1 }}>{task.title}</span>
                    {selectedPersonas.length > 1 && (
                      <select
                        value={task.assignee}
                        onChange={e => {
                          setPhases(prev => prev.map(p => p.id === phase.id ? {
                            ...p, tasks: p.tasks.map(t => t.id === task.id ? { ...t, assignee: e.target.value } : t)
                          } : p))
                        }}
                        style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: 'var(--bg-elevated)', color: 'var(--text-muted)', border: '1px solid var(--border)', fontFamily: 'inherit' }}
                      >
                        <option value="">All</option>
                        {selectedPersonas.map(n => <option key={n} value={n}>{n.charAt(0).toUpperCase() + n.slice(1)}</option>)}
                      </select>
                    )}
                    <button
                      onClick={() => setPhases(prev => prev.map(p => p.id === phase.id ? { ...p, tasks: p.tasks.filter(t => t.id !== task.id) } : p))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 10 }}
                    >✕</button>
                  </div>
                ))}
                <input
                  placeholder="Add a task..."
                  onKeyDown={e => {
                    if (e.key === 'Enter' && (e.target as HTMLInputElement).value.trim()) {
                      const val = (e.target as HTMLInputElement).value.trim()
                      setPhases(prev => prev.map(p => p.id === phase.id ? {
                        ...p, tasks: [...p.tasks, { id: `tk-${Date.now()}`, title: val, assignee: '' }]
                      } : p))
                      ;(e.target as HTMLInputElement).value = ''
                    }
                  }}
                  style={{ ...inputStyle, padding: '6px 10px', fontSize: 11, marginTop: 6 }}
                />
              </div>
            ))}

            {/* Quick add task (no phase) */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input
                value={quickTask}
                onChange={e => setQuickTask(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && quickTask.trim()) {
                    let standalone = phases.find(p => p.id === 'standalone')
                    if (!standalone) {
                      standalone = { id: 'standalone', title: 'Tasks', tasks: [] }
                      setPhases(prev => [...prev, standalone!])
                    }
                    setPhases(prev => prev.map(p => p.id === 'standalone' ? { ...p, tasks: [...p.tasks, { id: `tk-${Date.now()}`, title: quickTask.trim(), assignee: '' }] } : p))
                    setQuickTask('')
                  }
                }}
                placeholder="Add a task..."
                style={{ ...inputStyle, flex: 1, padding: '8px 12px', fontSize: 12 }}
              />
              {selectedPersonas.length > 1 && quickTask.trim() && (
                <span style={{ fontSize: 9, color: 'var(--text-muted)', alignSelf: 'center' }}>assign after adding</span>
              )}
            </div>

            {/* Add phase */}
            {addingPhase ? (
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  value={newPhaseTitle}
                  onChange={e => setNewPhaseTitle(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && newPhaseTitle.trim()) {
                      setPhases(prev => [...prev, { id: `ph-${Date.now()}`, title: newPhaseTitle.trim(), tasks: [] }])
                      setNewPhaseTitle('')
                      setAddingPhase(false)
                    }
                  }}
                  placeholder="Phase title..."
                  style={{ ...inputStyle, flex: 1, padding: '6px 10px', fontSize: 11 }}
                  autoFocus
                />
                <button onClick={() => {
                  if (newPhaseTitle.trim()) {
                    setPhases(prev => [...prev, { id: `ph-${Date.now()}`, title: newPhaseTitle.trim(), tasks: [] }])
                    setNewPhaseTitle('')
                  }
                  setAddingPhase(false)
                }} style={{ padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: '#856FC8', color: 'white', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>Add</button>
                <button onClick={() => setAddingPhase(false)} style={{ padding: '6px 12px', borderRadius: 6, fontSize: 11, background: 'var(--bg-elevated)', color: 'var(--text-muted)', border: '1px solid var(--border)', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
              </div>
            ) : (
              <button
                onClick={() => setAddingPhase(true)}
                style={{ fontSize: 11, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: '4px 0' }}
              >
                + Add Phase
              </button>
            )}
          </div>

          {/* Difficulty */}
          <div>
            <label style={labelStyle}>Difficulty</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {['easy', 'medium', 'hard', 'expert'].map(d => (
                <button
                  key={d}
                  onClick={() => setDifficulty(d)}
                  style={{
                    padding: '6px 16px', borderRadius: 8, fontSize: 12,
                    background: difficulty === d ? 'var(--bg-elevated)' : 'transparent',
                    color: difficulty === d ? 'var(--text-primary)' : 'var(--text-muted)',
                    border: `1px solid ${difficulty === d ? 'var(--border-strong, #3a3a44)' : 'var(--border)'}`,
                    cursor: 'pointer', fontFamily: 'inherit',
                    textTransform: 'capitalize',
                  }}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          {/* Submit */}
          <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              style={{
                padding: '12px 32px', borderRadius: 8, fontSize: 14, fontWeight: 600,
                background: canSubmit ? '#856FC8' : 'var(--bg-elevated)',
                color: canSubmit ? 'white' : 'var(--text-muted)',
                border: 'none', cursor: canSubmit ? 'pointer' : 'default',
                fontFamily: 'inherit',
              }}
            >
              {submitting ? 'Creating...' : 'Create Quest'}
            </button>
            <button
              onClick={() => navigate('/evolve')}
              style={{
                padding: '12px 24px', borderRadius: 8, fontSize: 14,
                background: 'transparent', color: 'var(--text-muted)',
                border: '1px solid var(--border)', cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function WizardChatPanel({ history, setHistory, input, setInput, sending, setSending, onClose, onQuestData, extraBody }: {
  history: { role: 'user' | 'assistant'; content: string }[]
  setHistory: (h: any) => void
  input: string
  setInput: (s: string) => void
  sending: boolean
  setSending: (b: boolean) => void
  onClose: (() => void) | null
  onQuestData: (data: any) => void
  extraBody?: Record<string, any>
}) {
  const [collapsed, setCollapsed] = useState(false)

  const sendMessage = async (msg: string) => {
    if (!msg.trim() || sending) return
    const newHistory = [...history, { role: 'user' as const, content: msg }]
    setHistory(newHistory)
    setInput('')
    setSending(true)
    try {
      const res = await fetch('/api/wizard/quest-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, history, ...(extraBody || {}) }),
      })
      const data = await res.json()
      if (data.reply) {
        setHistory([...newHistory, { role: 'assistant', content: data.reply }])
        if (data.questData) onQuestData(data.questData)
      } else {
        setHistory([...newHistory, { role: 'assistant', content: `Error: ${data.error || 'No response'}` }])
      }
    } catch {
      setHistory([...newHistory, { role: 'assistant', content: 'Connection error — please try again' }])
    }
    setSending(false)
  }

  return (
    <div style={{
      marginBottom: 16, borderRadius: 12, overflow: 'hidden',
      background: 'var(--bg-surface)', border: '1px solid #856FC844',
      boxShadow: collapsed ? 'none' : '0 4px 20px rgba(133,111,200,0.15)',
    }}>
      {/* Header bar — always visible */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        style={{
          width: '100%', padding: '12px 16px',
          display: 'flex', alignItems: 'center', gap: 10,
          background: 'linear-gradient(135deg, rgba(133,111,200,0.08), rgba(92,200,132,0.04))',
          border: 'none', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
          borderBottom: collapsed ? 'none' : '1px solid var(--border)',
        }}
      >
        <span style={{
          transition: 'transform 0.2s',
          transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)',
          display: 'inline-block', fontSize: 10, color: 'var(--text-muted)',
        }}>▶</span>
        <span style={{ fontSize: 18 }}>🧙</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#856FC8' }}>Quest Wizard</span>
        {history.length > 0 && (
          <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 4 }}>
            {Math.ceil(history.length / 2)} exchange{Math.ceil(history.length / 2) !== 1 ? 's' : ''}
          </span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--text-muted)' }}>
          {collapsed ? 'click to expand' : 'click to minimize'}
        </span>
        {onClose && <button
          onClick={e => { e.stopPropagation(); onClose() }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 12, padding: '0 4px' }}
        >✕</button>}
      </button>

      {/* Expandable chat body */}
      {!collapsed && (
        <>
          <div style={{ maxHeight: 350, overflowY: 'auto', padding: 16 }}>
            {history.length === 0 && (
              <div style={{ padding: '24px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.3 }}>🧙</div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 4 }}>
                  Tell me what quest you want to create
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', opacity: 0.6 }}>
                  Describe the persona, the goal, and what you want them to deliver.
                </div>
              </div>
            )}
            {history.map((msg, i) => (
              <div key={i} style={{ marginBottom: 12, textAlign: msg.role === 'user' ? 'right' : 'left' }}>
                <div style={{
                  display: 'inline-block', maxWidth: '85%', padding: '10px 14px',
                  borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                  background: msg.role === 'user' ? '#856FC8' : 'var(--bg-elevated)',
                  color: msg.role === 'user' ? 'white' : 'var(--text-primary)',
                  fontSize: 13, lineHeight: 1.6, textAlign: 'left',
                }}>
                  {msg.role === 'assistant' ? <MarkdownRenderer content={msg.content} /> : msg.content}
                </div>
              </div>
            ))}
            {sending && (
              <div style={{ color: '#856FC8', fontSize: 12, padding: '8px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ animation: 'xp-pulse 1.5s ease-in-out infinite' }}>🧙</span> Wizard is thinking...
              </div>
            )}
          </div>

          <div style={{ borderTop: '1px solid var(--border)', padding: 12, display: 'flex', gap: 8 }}>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey && input.trim() && !sending) {
                  e.preventDefault()
                  sendMessage(input.trim())
                }
              }}
              placeholder="Describe what quest you want..."
              rows={2}
              style={{
                flex: 1, padding: '10px 14px', fontSize: 13, lineHeight: 1.5,
                background: 'var(--bg-elevated)', color: 'var(--text-primary)',
                border: '1px solid var(--border)', borderRadius: 8,
                fontFamily: 'inherit', outline: 'none', resize: 'none',
              }}
            />
            <button
              onClick={() => sendMessage(input.trim())}
              disabled={!input.trim() || sending}
              style={{
                padding: '10px 20px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                background: input.trim() ? '#856FC8' : 'var(--bg-elevated)',
                color: input.trim() ? 'white' : 'var(--text-muted)',
                border: 'none', cursor: input.trim() ? 'pointer' : 'default',
                fontFamily: 'inherit', alignSelf: 'flex-end',
              }}
            >
              {sending ? '...' : 'Send'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 600,
  color: 'var(--text-secondary)', marginBottom: 6,
  textTransform: 'uppercase', letterSpacing: '0.5px',
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 14px', fontSize: 13, borderRadius: 8,
  background: 'var(--bg-elevated)', color: 'var(--text-primary)',
  border: '1px solid var(--border)', fontFamily: 'inherit',
  outline: 'none',
}
